/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpEvent,
  HttpErrorResponse,
  HttpHandlerFn
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AuthFacade } from '~console/app/~store/facades/auth.facade';
import { GlobalFacade } from '~console/app/~store/facades/global.facade';
import {
  ORGANIZATION_DISABLED_ERROR,
  SHOW_ERROR_INTERCEPTOR_FOR_401,
  SHOW_ERROR_INTERCEPTOR_FOR_403,
  SKIP_ERROR_INTERCEPTOR
} from '~console/app/~shared/constants/global.constants';
import { extractFromRequestData } from '../utils/utils';
import { ToastService } from '../services/toast.service';

export function httpErrorsInterceptor(
  request: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> {
  const authFacade = inject(AuthFacade);
  const globalFacade = inject(GlobalFacade);
  const toastService = inject(ToastService);
  const { request: req, value: useStateFormViolationHandler } =
    extractFromRequestData<boolean>(request, 'useStateFormViolationHandler');

  const { value: skipErrorInterceptor } = extractFromRequestData<boolean>(
    request,
    'skipErrorInterceptor'
  );

  return next(req).pipe(
    tap({
      error: error => {
        if (useStateFormViolationHandler) {
          globalFacade.handleFormViolations(error);
        }
      }
    }),
    catchError((error: HttpErrorResponse) => {
      if (
        request.context.get(SKIP_ERROR_INTERCEPTOR) === true ||
        skipErrorInterceptor
      ) {
        return throwError(() => error);
      }

      if (req.method === 'POST' && error.status === 409) {
        const detailMessage =
          typeof error.error === 'string'
            ? error.error
            : error.error?.detail || error.message;

        if (detailMessage) {
          toastService.open({
            content: detailMessage,
            type: 'error',
            config: {
              dismissible: true,
              autoClose: false
            }
          });
        }

        return throwError(() => error);
      }

      if (
        error.status === 401 &&
        request.context.get(SHOW_ERROR_INTERCEPTOR_FOR_401) !== true
      ) {
        return throwError(() => error);
      }

      if (error.status === 403) {
        const wwwAuthenticate = error.headers.get('WWW-Authenticate');
        if (
          wwwAuthenticate?.includes(`error="${ORGANIZATION_DISABLED_ERROR}"`)
        ) {
          toastService.open({
            content: `Votre organisation a été désactivée. Vous allez être déconnecté.`,
            type: 'error'
          });
          authFacade.logout();
          return throwError(() => error);
        }

        if (request.context.get(SHOW_ERROR_INTERCEPTOR_FOR_403) !== true) {
          return throwError(() => error);
        }
      }

      if (error.error) {
        if (error.status === 0) {
          toastService.open({
            content: `Veuillez vérifier votre accès à internet`,
            type: 'error'
          });
        } else if (!error.error.violations) {
          toastService.open({
            content:
              error.error.detail ||
              error.error.title ||
              `Une erreur s'est produite. Veuillez réessayer plus tard.`,
            type: 'error'
          });
        }
      }

      if (error.status >= 500) {
        toastService.open({
          content: `Une erreur s'est produite veuillez rééssayer plus tard`,
          type: 'error'
        });
      }

      return throwError(() => error);
    })
  );
}
